import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/*
 * Shared preview/download handling for lead attachments (Notes and Files tabs).
 *
 * An "item" is { id, fileName, fileType, fileUrl, fetchBlob }:
 *  - fileUrl is the public S3 URL. It is used directly — going through the API's
 *    download route would redirect to S3, which has no CORS headers, so the
 *    browser blocks the XHR.
 *  - fetchBlob() loads files kept on the API's local disk through the
 *    authenticated route (so the JWT is sent); they are shown from a blob URL.
 */

export const getAttachmentKind = (item) => {
  const type = item.fileType || "";
  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf") return "pdf";
  if (type.startsWith("audio/")) return "audio";
  return "file";
};

export const isPreviewable = (item) => getAttachmentKind(item) !== "file";

export const useAttachmentFiles = () => {
  const [blobUrls, setBlobUrls] = useState({});
  const createdUrlsRef = useRef([]);
  const requestedRef = useRef(new Set());

  useEffect(
    () => () =>
      createdUrlsRef.current.forEach((url) => window.URL.revokeObjectURL(url)),
    []
  );

  const loadBlobUrl = async (item) => {
    if (blobUrls[item.id]) return blobUrls[item.id];
    const blob = await item.fetchBlob();
    const url = window.URL.createObjectURL(blob);
    createdUrlsRef.current.push(url);
    setBlobUrls((prev) => ({ ...prev, [item.id]: url }));
    return url;
  };

  const srcFor = (item) => item.fileUrl || blobUrls[item.id];

  // Fetches local-disk images once so they can be shown as thumbnails
  const loadThumbnails = (items) =>
    items.forEach((item) => {
      if (
        item.fileUrl ||
        getAttachmentKind(item) !== "image" ||
        requestedRef.current.has(item.id)
      )
        return;
      requestedRef.current.add(item.id);
      loadBlobUrl(item).catch((err) =>
        console.error("Error loading attachment preview:", err)
      );
    });

  const download = async (item) => {
    if (item.fileUrl) {
      window.open(item.fileUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const url = await loadBlobUrl(item);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", item.fileName || "attachment");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return { srcFor, loadBlobUrl, loadThumbnails, download };
};

// Square image thumbnail; shows a spinner until a local-disk image has loaded
export const AttachmentThumbnail = ({ src, alt, size = 88, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: 1.5,
      border: "1px solid #e5e7eb",
      cursor: "pointer",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "#f9fafb",
    }}
  >
    {src ? (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    ) : (
      <CircularProgress size={Math.min(18, size / 2)} />
    )}
  </Box>
);

// Modal viewer for images, PDFs and audio, with prev/next across `items`
export const AttachmentPreviewDialog = ({
  items,
  activeId,
  onChange,
  onClose,
  files,
  onError,
}) => {
  const previewItems = items.filter(isPreviewable);
  const index = previewItems.findIndex((item) => item.id === activeId);
  const active = previewItems[index];
  const src = active && files.srcFor(active);

  const step = (delta) =>
    onChange(
      previewItems[(index + delta + previewItems.length) % previewItems.length].id
    );

  // Fetch the file if it isn't loaded yet (PDF/audio, or a thumbnail still loading)
  useEffect(() => {
    if (!active || src) return;
    files.loadBlobUrl(active).catch((err) => {
      console.error("Error loading attachment preview:", err);
      onError?.("Failed to load attachment preview.");
      onClose();
    });
  }, [activeId]);

  // Arrow keys move between attachments while open
  useEffect(() => {
    if (!active || previewItems.length < 2) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const handleDownload = () =>
    files.download(active).catch((err) => {
      console.error("Error downloading attachment:", err);
      onError?.("Failed to download attachment.");
    });

  return (
    <Dialog open={Boolean(active)} onClose={onClose} maxWidth="lg" fullWidth>
      {active && (
        <>
          <DialogTitle sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              noWrap
              sx={{ fontSize: 15, fontWeight: 600, flex: 1, minWidth: 0 }}
            >
              {active.fileName}
            </Typography>
            {previewItems.length > 1 && (
              <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                {index + 1} / {previewItems.length}
              </Typography>
            )}
            <Tooltip title="Download">
              <IconButton size="small" onClick={handleDownload}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <Divider sx={{ m: 0 }} />

          <DialogContent
            sx={{
              p: 0,
              position: "relative",
              bgcolor: "#111827",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
            }}
          >
            {!src ? (
              <CircularProgress sx={{ color: "#ffffff" }} />
            ) : getAttachmentKind(active) === "image" ? (
              <Box
                component="img"
                src={src}
                alt={active.fileName}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "78vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : getAttachmentKind(active) === "pdf" ? (
              <Box
                component="iframe"
                src={src}
                title={active.fileName}
                sx={{ width: "100%", height: "78vh", border: 0, bgcolor: "#ffffff" }}
              />
            ) : (
              <Box sx={{ p: 4, width: "100%", maxWidth: 520 }}>
                <audio controls autoPlay src={src} style={{ width: "100%" }} />
              </Box>
            )}

            {previewItems.length > 1 &&
              [
                { delta: -1, side: "left", Icon: ChevronLeftIcon },
                { delta: 1, side: "right", Icon: ChevronRightIcon },
              ].map(({ delta, side, Icon }) => (
                <IconButton
                  key={side}
                  onClick={() => step(delta)}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    [side]: 8,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.85)",
                    "&:hover": { bgcolor: "#ffffff" },
                  }}
                >
                  <Icon />
                </IconButton>
              ))}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};
